<template>
	<section>
		<!-- Events List -->
		<div class="row">
			<div class="col-xs-12 col-xl-6">
				<a v-for="(event, eventIdx) in eventsWithImage" class="card card-event is-featured" :key="eventIdx" :href="event.url">
					<div class="card-header" :style="{ 'background-image': event.imageUrl }">
						<p class="card-date">
							<time class="sr-only" :datetime="event.start">{{ event.dateFull }}</time>
							<time class="time-start" :datetime="event.start" aria-hidden="true">
								<span class="event-day">{{ event.dateDayOfWeekAbbr }}</span>
								<span class="event-date">{{ event.dateDayOfMonth }}</span>
								<span class="event-month">{{ event.dateMonthAbbr }}</span>
							</time>
						</p>
					</div>

					<div class="card-block">
						<p class="card-title">{{ event.title }}</p>
						<p class="card-text" v-if="event.featured">{{ event.description }}</p>
						<p class="card-time"><span class="ico-clock-sm"></span><TemporalDisplay :start="event.startMoment" :end="event.endMoment" template="time"/></p>
						<p class="card-location"><span class="ico-map-marker-sm"></span>{{ event.location }}</p>
						<p class="card-price" v-if="event.isPaidClass"><span class="ico-cost-sm"></span>Paid Event</p>
						<p class="card-register" v-if="event.isRegistrable"><span class="ico-calendar-checked"></span>Reserve</p>
					</div>
				</a>
			</div>

			<div class="col-xs-12 col-xl-6">
				<div class="row">
					<div v-for="(event, eventIdx) in eventsNoImage" class="col-xs-12 col-lg-6 col-xl-12" :key="eventIdx">
						<a class="card card-event" :href="event.url">
							<div class="card-header">
								<p class="card-date">
									<time class="sr-only" :datetime="event.start">{{ event.dateFull }}</time>
									<time class="time-start" :datetime="event.start" aria-hidden="true">
										<span class="event-day">{{ event.dateDayOfWeekAbbr }}</span>
										<span class="event-date">{{ event.dateDayOfMonth }}</span>
										<span class="event-month">{{ event.dateMonthAbbr }}</span>
									</time>
								</p>
							</div>
							<div class="card-block">
								<p class="card-title">{{ event.title }}</p>
								<p class="card-time"><span class="ico-clock-sm"></span><TemporalDisplay :start="event.startMoment" :end="event.endMoment" template="time"/></p>
								<p class="card-location"><span class="ico-map-marker-sm"></span>{{ event.location }}</p>
								<p class="card-price" v-if="event.isPaidClass"><span class="ico-cost-sm"></span>Paid Event</p>
								<p class="card-register" v-if="event.isRegistrable"><span class="ico-calendar-checked"></span>Reserve</p>
							</div>
						</a>
					</div>

					<!-- No Other Events -->
					<div class="col-xs-12 col-lg-6 col-xl-12" v-if="eventsNoImage.length === 0 || eventsNoImage.length === 1">
						<div class="card card-event is-empty" :class="{'other-events-zero': eventsNoImage.length === 0, 'other-events-one': eventsNoImage.length === 1}">
							<p class="card-text">No other events are scheduled at this time.</p>
						</div>
					</div>

				</div>
			</div>

			<!-- No Events -->
			<div class="card card-event is-empty" v-if="!eventsWithImage.length && !eventsNoImage.length && show">
				<p class="card-text">There are no events.</p>
			</div>
		</div>

	</section>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';
import moment from 'moment';
import { baseComponentMixin } from '@/mixins/baseComponent.mixin';
import { UxSchedulesApi } from '@/apis/uxSchedules.api';
import { FrameworkService } from '@/services/framework.service';
import TemporalDisplay from '@/components/schreg-common/TemporalDisplay.vue';
import { momentProvider } from '@/services/clock.service';
import { StateService } from '@/services/state.service';

export default defineComponent({
	name: 'EventsBriefComponent',
	config: {
		key: 'schreg.eventsBrief',
		devPath: 'events-brief',
		exported: true
	},
	components:{
		TemporalDisplay
	},
	mixins : [
		baseComponentMixin
	],
	data() {
		return {
			eventsWithImage: [],
			eventsNoImage: [],
			fullEventsUrl: '',
			show: false,
			isWorkExperience: FrameworkService.isWorkExperience()
		};
	},
	mounted() {
		const stores = FrameworkService.isWorkExperience()
			? ['ClubStore', 'UrlStore', 'ProspectProfileStore']
			: ['ClubStore', 'ClubsStore', 'ProfileStore', 'UrlStore'];

		this.getFrameworkData(this.completeMounting, stores);
	},
	methods: {
		completeMounting() {
			let searchFilters = {
				startDate: momentProvider().format('YYYY-MM-DD HH:mm:ss'),
				tags: [],
				endDate: momentProvider().add(import.meta.env.VITE_MAX_DAYS_EVENTS_FORWARD, 'days').format('YYYY-MM-DD HH:mm:ss'),
				freeOnly: false,
				locations: [
					StateService.getLocationName()
				]
			};

			if (StateService.authoredSettings) {
				if (StateService.authoredSettings.featuredOnly) {
					searchFilters.tags.push('featured:Event');
				}
				if (StateService.authoredSettings.interest) {
					if (StateService.authoredSettings.interest instanceof Array) {
						_.forEach(StateService.authoredSettings.interest, function(interest) {
							searchFilters.tags.push('interest:' + interest);
						});
					} else {
						searchFilters.tags.push('interest:' + StateService.authoredSettings.interest);
					}
				}
				if (StateService.authoredSettings.format) {
					if (StateService.authoredSettings.format instanceof Array) {
						_.forEach(StateService.authoredSettings.format, function(format) {
							searchFilters.tags.push('format:' + format);
						});
					} else {
						searchFilters.tags.push('format:' + StateService.authoredSettings.format);
					}
				} else {
					searchFilters.tags.push('format:Event');
				}
			}
			this.loadEvents(searchFilters);
		},

		loadEvents(searchFilters) {
			UxSchedulesApi
				.getEventsBrief(searchFilters)
				.then(response => {
					if (!response.ok) {
						return this.mountServerError(true, response.data.error);
					}

					const events = response.data.results;

					this.setupEventsData(events);

					this.eventsWithImage = events.slice(0, 1);
					this.eventsNoImage = events.slice(1, 3);

					this.show = true;
				});
		},

		setupEventsData(events) {
			_.forEach(events, (event) => {
				event.startMoment = moment.parseZone(event.start, moment.ISO_8601);
				event.endMoment = moment.parseZone(event.end, moment.ISO_8601);
				event.dateFull = event.startMoment.format('dddd, MMM D');
				event.dateDayOfWeekAbbr = event.startMoment.format('ddd');
				event.dateDayOfMonth = event.startMoment.format('D');
				event.dateMonthAbbr = event.startMoment.format('MMM');
				event.url = FrameworkService.eventUrl(event.id);
				event.imageUrl = event.featured ? `url(${event.imageUrl})` : 'none';
				event.distance = event.distance.toFixed(1);
			});
		}
	}
});
</script>

<style lang="scss" scoped>
@import "@/common/styles/panels.scss";
@import "@/common/styles/titleSection.scss";
@import "@/common/styles/loading.scss";
@import "styles/eventsBrief.scss";
</style>
